import { useState } from 'react';

export default function Hero() {
  const [showLeadForm, setShowLeadForm] = useState(false);

  const handleLeadFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    try {
      await fetch('https://readdy.ai/api/form/d5pr1vihs78ip537014g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      });
      form.reset();
      setShowLeadForm(false);
    } catch (error) {
      // silent
    }
  };

  return (
    <>
      <section className="relative min-h-screen flex items-center overflow-hidden bg-[#0a0f1e]">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/036afb5968fdc40cdef5e5c73e11e5df.jpg"
            alt="AI Technology Background"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0f1e] via-[#0a0f1e]/80 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1e] via-transparent to-transparent"></div>
        </div>

        {/* Animated grid overlay */}
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'linear-gradient(rgba(59,130,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.3) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}
        ></div>

        {/* Floating orbs */}
        <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-blue-600/20 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/3 right-1/3 w-48 h-48 bg-indigo-500/20 rounded-full filter blur-2xl animate-pulse" style={{ animationDelay: '1s' }}></div>

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-2 mb-8">
              <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-blue-300">AI-Powered Outsourcing — The Future is Now</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Transforming Businesses,
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                Intelligently
              </span>
            </h1>

            <p className="text-xl sm:text-2xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              We combine elite human talent with cutting-edge AI to eliminate inefficiencies, automate workflows, and reallocate your resources where they matter most.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-16">
              <button
                onClick={() => setShowLeadForm(true)}
                className="bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all transform hover:scale-105 whitespace-nowrap shadow-lg shadow-blue-900/50"
              >
                Get Free Consultation
              </button>
              <a
                href="/services"
                className="border border-white/30 hover:border-blue-400 hover:text-blue-400 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all whitespace-nowrap backdrop-blur-sm"
              >
                Explore AI Solutions
              </a>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-gray-500">
          <span className="text-xs tracking-widest uppercase">Scroll</span>
          <div className="w-px h-8 bg-gradient-to-b from-gray-500 to-transparent animate-pulse"></div>
        </div>
      </section>

      {/* Lead Capture Modal */}
      {showLeadForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-8 relative shadow-2xl">
            <button
              onClick={() => setShowLeadForm(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Get Your Free Consultation</h2>
            <p className="text-gray-600 mb-6">Tell us about your needs and we'll design a custom AI-powered solution within 24 hours.</p>
            <form id="hero-lead-form" data-readdy-form onSubmit={handleLeadFormSubmit} className="space-y-4">
              <div>
                <label htmlFor="hero-name" className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input type="text" id="hero-name" name="name" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
              </div>
              <div>
                <label htmlFor="hero-email" className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                <input type="email" id="hero-email" name="email" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
              </div>
              <div>
                <label htmlFor="hero-company" className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input type="text" id="hero-company" name="company" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
              </div>
              <div>
                <label htmlFor="hero-service" className="block text-sm font-medium text-gray-700 mb-1">Service Needed *</label>
                <select id="hero-service" name="service" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                  <option value="">Select a service</option>
                  <option value="AI Customer Experience">AI Customer Experience</option>
                  <option value="AI Back Office Automation">AI Back Office Automation</option>
                  <option value="AI Sales & Lead Generation">AI Sales &amp; Lead Generation</option>
                  <option value="AI Technical Support">AI Technical Support</option>
                  <option value="Workforce Automation">Workforce Automation</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label htmlFor="hero-message" className="block text-sm font-medium text-gray-700 mb-1">Tell us about your project</label>
                <textarea id="hero-message" name="message" rows={3} maxLength={500} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" placeholder="Describe your requirements..."></textarea>
                <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
              </div>
              <button type="submit" className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold whitespace-nowrap shadow-lg">
                Get My Free Consultation
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
